/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
    Card,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    MoreButton,
    Table,
} from "@jhool-io/fe-components";
import { ref, remove } from "firebase/database";
import { useSearchParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import useToast from "../../../../hooks/useToast";
import { rtdb } from "../../../../firebase";
import { IThirtydaysProvider } from "../../types/thirtydays-providers";
import {
    displayNameInRightFormat,
    formatDate,
} from "../../../../utils/helpers";
import {
    useCanManageThirtyDaysProviders,
    useThirtydaysProviders,
} from "../../../../hooks/useThirtydaysProviders";
import ListState from "../../../../components/ListState/ListState";
import NewThirtydaysProviderForm from "../NewThirtydaysProviderForm/NewThirtydaysProviderForm";
import { useAppSelector } from "../../../../hooks/useRedux";

type DialogTypes = "add" | "delete";

export default function ThirtydaysProvidersList() {
    const [dialogInView, setDialogInView] = React.useState<DialogTypes | null>(
        null
    );
    const [providerInViewId, setProviderInViewId] = React.useState<
        string | null
    >(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const addingProvider = searchParams.get("adding_provider");

    const { practice } = useAppSelector((state) => state.userPractice);

    const { providers } = useThirtydaysProviders();

    const canManageThirtyDaysProviders = useCanManageThirtyDaysProviders();

    const { toast } = useToast();

    const columns: ColumnDef<IThirtydaysProvider>[] = React.useMemo(
        () => [
            {
                accessorKey: "provider_name",
                header: "Provider name",
                cell: ({ row }) => (
                    <span className="capitalize">
                        {displayNameInRightFormat(row.original.provider_name)}
                    </span>
                ),
            },
            {
                accessorKey: "created_by",
                header: "Added By",
                cell: ({ row }) =>
                    row.original.created_by
                        ? displayNameInRightFormat({
                              firstName: row.original.created_by.first_name,
                              lastName: row.original.created_by.last_name,
                          })
                        : "--",
            },
            {
                accessorKey: "created_at",
                header: "Created at",
                cell: ({ row }) =>
                    row.original.created_at
                        ? formatDate(
                              row.original.created_at,
                              false,
                              "MM/dd/yyyy, hh:mmaaa"
                          )
                        : "--",
            },
            {
                accessorKey: "id",
                header: "",
                cell: ({ row }) =>
                    canManageThirtyDaysProviders && (
                        <Dropdown>
                            <DropdownTrigger asChild>
                                <MoreButton />
                            </DropdownTrigger>
                            <DropdownContent width="auto" align="end">
                                <DropdownItem
                                    onClick={() => {
                                        setProviderInViewId(row.original.id);
                                        setDialogInView("delete");
                                    }}
                                >
                                    Delete
                                </DropdownItem>
                            </DropdownContent>
                        </Dropdown>
                    ),
            },
        ],
        [canManageThirtyDaysProviders]
    );

    const handleCloseDialog = () => {
        setDialogInView(null);

        searchParams.delete("adding_provider");
        setSearchParams(searchParams);
    };

    const handleDeleteProvider = async () => {
        try {
            const dbRef = ref(
                rtdb,
                `/thirtydaysproviders/${practice?.practice_id}/${providerInViewId}`
            );

            await remove(dbRef);
            toast({
                mode: "success",
                message: "Provider removed successfully",
            });
            setDialogInView(null);
            setProviderInViewId(null);
        } catch {
            toast({
                mode: "error",
                message:
                    "Could not remove provider at this time, please try again later",
            });
        }
    };

    return (
        <>
            <AlertDialog open={dialogInView === "delete"}>
                <AlertDialogContent
                    onCancelClick={() => {
                        setDialogInView(null);
                        setProviderInViewId(null);
                    }}
                    title="  Are you sure you want to delete provider?"
                    description=" This will delete this provider from the list and
            cannot be reversed"
                    saveText="Delete provider"
                    onSaveClick={handleDeleteProvider}
                    isDeleting
                />
            </AlertDialog>
            <Dialog open={dialogInView === "add"}>
                <DialogContent
                    handleCloseDialog={handleCloseDialog}
                    variant="center"
                    title="Add provider"
                    saveText="Add provider"
                    submitBtnFormValue="add-provider"
                    isSubmitBtnDisabled={Boolean(addingProvider)}
                    isCancelBtnDisabled={Boolean(addingProvider)}
                >
                    <NewThirtydaysProviderForm
                        onFormSubmit={handleCloseDialog}
                    />
                </DialogContent>
            </Dialog>
            <div>
                {canManageThirtyDaysProviders && (
                    <Card classNames="mb-[--spacing-header] gap-[--spacing-items] flex justify-end items-center flex-wrap">
                        <Button onClick={() => setDialogInView("add")}>
                            Add new
                        </Button>
                    </Card>
                )}
                {providers && (
                    <div>
                        {providers.length === 0 && (
                            <ListState
                                isError={false}
                                stateHelperText="No provider added yet, add one to get started"
                                emptyMessage="No provider to show"
                            />
                        )}
                        {providers.length > 0 && (
                            <Table columns={columns} data={providers || []} />
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
